/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: caoran
 * @LastEditTime: 2026-05-28 16:45:24
 */

// 声明 Vite define 定义的全局变量
import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react'
import './index.scss'
import {
  Layout,
  Button,
  Dropdown,
  Avatar,
  Modal,
  Tooltip,
  message,
  Popover,
  Input
} from 'antd'
import type { MenuProps } from 'antd'
import { DownOutlined, VerticalAlignBottomOutlined } from '@ant-design/icons'
import GroupInfo from './GroupInfo'
import Statistics from './Statistics'
import NewStatistics from './NewStatistics'
import VipHoverPopover from './VipHoverPopover'
import VipEntranceBanner from './VipEntranceBanner'
import VipEntranceHoverCard from './VipEntranceHoverCard'
import { fetchAndStorePhotoStatistics } from '../../../actions/photo'
import {
  useAppDispatch,
  useAppSelector,
  usePayment,
  useVip
} from '../../../app/hooks'
import {
  logout,
  setPaymentVisible,
  getOSSToken,
  finishNickname
} from '../../../actions/main'
import sensors, { abtest } from '../../../utils/sensors'
import CONFIG from '../../../constants/config'
import dayjs from 'dayjs'
import desktopLib from '../../../utils/desktopLib'
import ContactUS from '../../components/ContactUS'
import mainHeaderVipImg from '../../../images/main-header-vip.png'
import createGroupeImage from '../../../images/create-group-text.png'
import NotifyButton from './NotifyButton'
import UploadPhotosButton from '../../components/UploadPhotos'
import global_border_help from '../../../images/header/global_border_help.png'
import CreateGroup from '../../components/CreateGroup'
import JoinGroup from '../../components/JoinGroup'
import { supportsNewDownloadFeature } from '../../../utils/versionUtils'
import { useVipKind, useAbFlag } from '../../../app/hooks'
import { fetchUpdateGoodsInfo } from '../../../actions/payment'
import EnterpriseAiUpgradeModal from './EnterpriseAiUpgradeModal'
import hejingQrCode from '../../../images/service-qrcode/hejing.png'
import todaycam_logo from '../../../images/todaycam_logo.png'

declare const __RELEASE_STAGE__: string

const { Header } = Layout

const BussinessArea = ({
  onPayMain,
  expireDays,
  vipType
}: {
  onPayMain?: () => void
  expireDays?: number
  vipType?: string
}) => {
  const group = useAppSelector((state) => state.group)
  const dispatch = useAppDispatch()
  const isGoVipTracked = useRef(false)
  const isRenewalTracked = useRef(false)
  const { isVip, isSVip, isVVip } = useVip()
  const myRole = group.myRole
  const viewPerm = group.viewPerm
  // 原 groupWebVipAB.entry 实验已全量，直接按角色判断
  const isGroupWebVipEntry =
    myRole > CONFIG.memberRoleTypes.NORMAL ||
    viewPerm === CONFIG.viewPermissionTypes.GROUP
  const [updateGoodsInfo, setUpdateGoodsInfo] = useState<any>(null)
  const statistics = group.statistics
  React.useEffect(() => {
    const groupId = group.groupId
    if (!groupId) return
    if (!statistics) {
      dispatch(fetchAndStorePhotoStatistics({ groupID: groupId }))
    }
  }, [group.groupId, statistics, dispatch])
  const usedExpireDays = typeof expireDays === 'number' ? expireDays : 0
  const isRenewal = useMemo(() => {
    return isVip && usedExpireDays <= 7
  }, [isVip, usedExpireDays])

  const onRenewal = useCallback(() => {
    dispatch(setPaymentVisible(true, 'groupRenewVIP'))
    sensors.track('web_get_action_button_menu_renew_vip', {
      actionType: 'clickRenew'
    })
  }, [])

  useEffect(() => {
    if (isRenewal && !isRenewalTracked.current) {
      sensors.track('web_get_action_button_menu_renew_vip', {
        actionType: 'show',
        groupID: group.groupId
      })
      isRenewalTracked.current = true
    }
  }, [isRenewal, group])

  useEffect(() => {
    if (!isGoVipTracked.current) {
      sensors.track('web_show_button_menu_go_vip', {
        groupID: group.groupId,
        vipType,
        number1: expireDays
      })
      isGoVipTracked.current = true
    }
  }, [group, isVip, vipType, expireDays])

  useEffect(() => {
    if (isVip) {
      dispatch(
        fetchUpdateGoodsInfo({
          groupID: group.groupId
        })
      )
        .then((data: any) => {
          setUpdateGoodsInfo(data.goodsInfo || [])
        })
        .catch(() => {
          setUpdateGoodsInfo([])
        })
    }
  }, [isVip, dispatch])

  // 新版权益入口（原 groupWebVipAB.entry 实验已全量）
  if (isGroupWebVipEntry) {
    let badgeText: string | null = null
    let btnText = ''

    if (!isVip) {
      if (usedExpireDays < 0) {
        btnText = '续费会员'
        badgeText = '低至 17元/月'
      } else {
        btnText = '开通会员'
        badgeText = '低至 17元/月'
      }
    } else if (isVip && isSVip && usedExpireDays > 0) {
      // 在期 SVIP && 非临期
      btnText = '会员中心'
      badgeText = null
    } else if (isVip && !isSVip && usedExpireDays > 30) {
      // 在期 VIP/VVIP && 非临期
      btnText = '升级会员'
      badgeText =
        updateGoodsInfo && updateGoodsInfo.length > 0 ? '补差价升级' : null
    } else if (isVip && expireDays === 0) {
      // 到期当日
      btnText = '续费会员'
      badgeText = '将于今日到期'
    } else {
      // 兜底
      btnText = isVVip ? '开通超级会员' : isVip ? '开通高级会员' : '开通会员'
    }
    if (isVip && usedExpireDays > 0 && usedExpireDays <= 30) {
      // 临期会员（1-30 天）
      btnText = '续费会员'
      badgeText = `将于${usedExpireDays}天后到期`
    }

    return (
      <div className="new-benefits-entry">
        <div className="pay-btn-wrapper">
          <Button className="pay-btn" onClick={onPayMain}>
            {btnText}
          </Button>
          {badgeText && <span className="pay-btn-badge">{badgeText}</span>}
        </div>
      </div>
    )
  }

  if (!isSVip) {
    return (
      <Button className="pay-btn" onClick={onPayMain}>
        {isVVip ? '开通超级会员' : isVip ? '开通高级会员' : '开通会员'}
      </Button>
    )
  }
  if (isRenewal) {
    return (
      <div className="renewal">
        <div>团队会员 {usedExpireDays}天后到期</div>
        <Button onClick={onRenewal}>立即续费</Button>
      </div>
    )
  }
  return null
}

const MainHeader = () => {
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.main.user)
  const group = useAppSelector((state) => state.group)
  const hasGroup = !!group.groupId
  const isDesktop = desktopLib.isDesktop
  const canUploadLogs = isDesktop && supportsNewDownloadFeature('1.3.2')
  const myRole = group.myRole
  const viewPerm = group.viewPerm
  const { isVip, isSVip, isVVip } = useVip()
  const isSpaceOpen = useAppSelector(
    (state: any) => state.mySpace?.personalSpace?.isSpaceOpen ?? false
  )
  const payment = usePayment()
  const currentVipKind = useVipKind()
  // 原 groupWebVipAB.entry / benifitPage 实验已全量，直接按角色判断
  const isGroupWebVipEntry =
    myRole > CONFIG.memberRoleTypes.NORMAL ||
    viewPerm === CONFIG.viewPermissionTypes.GROUP
  // AB 实验：webVipEntrance
  // - 顶部 banner：仅非 SVIP 命中才走新版；SVIP 顶部保留 NewStatistics + BussinessArea
  // - hover 卡：命中即走新版（含 SVIP —— SVIP 顶部维持老版，仅 hover 换新卡）
  const webVipEntranceAb = useAbFlag('webVipEntrance')
  const showVipEntranceBanner = webVipEntranceAb && !isSVip
  const showVipEntranceHoverCard = webVipEntranceAb
  const [createGroupModuleVisible, setCreateGroupModuleVisible] =
    useState(false)
  const [joinGroupModuleVisible, setJoinGroupModuleVisible] = useState(false)
  const [createFromPlace, setCreateFromPlace] = useState('webTopGuide')
  const [joinFromPlace, setJoinFromPlace] = useState('webTopGuide')
  const [enterpriseAiUpgradeVisible, setEnterpriseAiUpgradeVisible] =
    useState(false)
  const [uploadingLogs, setUploadingLogs] = useState(false)
  const [vipHover, setVipHover] = useState(false)
  const [nicknameModalVisible, setNicknameModalVisible] = useState(false)
  const [nicknameValue, setNicknameValue] = useState('')
  const nicknameModalAutoShownRef = useRef(false)
  const noGroupGuideShownRef = useRef(false)

  useEffect(() => {
    if (!nicknameModalAutoShownRef.current && user && user.nickname === '') {
      nicknameModalAutoShownRef.current = true
      setNicknameModalVisible(true)
    }
  }, [user])

  // 命中实验、要展示新版 banner/hover 卡时，强制拉一次 /stat。原因：
  // 1) 非 SVIP 命中走新 banner，顶部不渲染 BussinessArea，statistics 无人拉取；
  // 2) 团队信息接口(query/group)会把 statistics 占坑成缺 totalNum/memberCount 的残缺
  //    对象，触发 fetchAndStorePhotoStatistics 的「已有就不拉」短路，导致 /stat 从不请求、
  //    照片与成员数恒为 0。故用 forceRefresh 绕过短路，保证新卡拿到真实统计。
  useEffect(() => {
    if (!showVipEntranceHoverCard || !group.groupId) return
    dispatch(fetchAndStorePhotoStatistics({ groupID: group.groupId }, true))
  }, [showVipEntranceHoverCard, group.groupId, dispatch])

  // 会员中心 hover 防误触：进入延迟 + 离开延迟（master ce58cbda）
  //  · enter 350ms 才打开，避免横向扫过头部时误触发
  //  · leave 250ms 后关闭，鼠标在容器与 Popover 之间小幅移动不瞬间合上
  //  · ref 存定时器避免 state 更新引发额外 render
  const VIP_HOVER_ENTER_DELAY = 350
  const VIP_HOVER_LEAVE_DELAY = 250
  const vipHoverTimerRef = useRef<number | null>(null)
  const cancelVipHoverTimer = useCallback(() => {
    if (vipHoverTimerRef.current !== null) {
      window.clearTimeout(vipHoverTimerRef.current)
      vipHoverTimerRef.current = null
    }
  }, [])
  const handleVipMouseEnter = useCallback(() => {
    cancelVipHoverTimer()
    vipHoverTimerRef.current = window.setTimeout(() => {
      setVipHover(true)
      vipHoverTimerRef.current = null
    }, VIP_HOVER_ENTER_DELAY)
  }, [cancelVipHoverTimer])
  const handleVipMouseLeave = useCallback(() => {
    cancelVipHoverTimer()
    vipHoverTimerRef.current = window.setTimeout(() => {
      setVipHover(false)
      vipHoverTimerRef.current = null
    }, VIP_HOVER_LEAVE_DELAY)
  }, [cancelVipHoverTimer])
  useEffect(() => () => cancelVipHoverTimer(), [cancelVipHoverTimer])

  // 数据反馈神策公共属性 groupID 80% 为空（registerPage 时机晚于 autoTrack 与
  // 大量页面事件），公共属性不可靠。这里从 redux group state 显式读，让数据
  // 同学能用 groupID 是否为空区分"已登录但无团队"用户的 click 行为。
  const trackCreateGuide = (actionType: string) => {
    sensors.track('web_get_action_create_workgroup_guide', {
      actionType,
      isLogin: true,
      groupID: group.groupId || '',
      track_sign:
        'aworkgroup.b4407.action.web_get_action_create_workgroup_guide',
      requirement_id: '1090'
    })
  }

  // user 加载完成才能判断；user_id 存在 = fetchUser 已返回。
  // groups 缺失 / 长度 0 都算"真无团队"。
  const userGroups = (user as any)?.groups
  const userId = (user as any)?.user_id
  useEffect(() => {
    if (!userId) return
    const groupCount = Array.isArray(userGroups) ? userGroups.length : 0
    if (groupCount === 0 && !noGroupGuideShownRef.current) {
      trackCreateGuide('show')
      noGroupGuideShownRef.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, userGroups])

  // ts target=es5 不支持 /\p{}/u 字面量，用 new RegExp 绕开编译期检查
  /* eslint-disable no-control-regex, prefer-regex-literals */
  const nicknameFilterRe = React.useMemo(
    () => ({
      special: new RegExp('[<>&"\'`#;\\\\\\x00-\\x1f\\x7f]', 'g'),
      emoji: new RegExp('\\p{Extended_Pictographic}', 'gu')
    }),
    []
  )
  /* eslint-enable no-control-regex, prefer-regex-literals */
  const handleNicknameInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const filtered = e.target.value
        .replace(nicknameFilterRe.special, '')
        .replace(nicknameFilterRe.emoji, '')
        .slice(0, 20)
      setNicknameValue(filtered)
    },
    [nicknameFilterRe]
  )

  const handleNicknameSubmit = useCallback(async () => {
    if (!nicknameValue) return
    try {
      await dispatch(
        finishNickname({ userID: userId, nickname: nicknameValue })
      )
      message.success('姓名修改成功')
      setNicknameModalVisible(false)
      setNicknameValue('')
    } catch {
      message.error('修改失败，请重试')
    }
  }, [dispatch, userId, nicknameValue])

  const onLogout = useCallback(() => {
    sensors.track('web_workgroup_nav_menu_cli', {
      path: 'quit',
      isGroupVIP: group.isVip,
      bool1: isSpaceOpen
    })
    dispatch(logout())
  }, [group, isSpaceOpen])
  const config = useAppSelector((state) => state.main.config)
  const liveStreamingConfig = config?.live_streaming_config
    ? JSON.parse(config.live_streaming_config)
    : {}

  useEffect(() => {
    if (isGroupWebVipEntry) {
      sensors.track(
        'vip_b4360_c7987_click',
        buildBannerTrackParams('enterPage')
      )
    }
  }, [isGroupWebVipEntry])

  const handleUploadLogs = useCallback(async () => {
    if (!desktopLib.isDesktop || uploadingLogs) return
    setUploadingLogs(true)
    const hide = message.loading('日志上报中...', 0)
    try {
      const ossToken = await dispatch(getOSSToken())
      const groupName = group.groupName
      const groupId = group.groupId
      const userId = user?.user_id
      const userName = user?.nickname
      await desktopLib.uploadLogs({
        ossToken,
        groupName,
        groupId,
        userId,
        userName
      })
      message.success('日志上报成功')
    } catch (error) {
      console.error('upload logs failed', error)
      message.error('日志上报失败, 请检查网络连接或稍后重试')
    } finally {
      hide()
      setUploadingLogs(false)
    }
  }, [dispatch, group, user, uploadingLogs])

  // antd 使用 items 属性代替 Menu.Item
  const menuItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = []
    if (hasGroup) {
      items.push({
        key: 'orders',
        label: '我的订单',
        onClick: () => {
          window.location.href = '/orders'
        }
      })
    }
    items.push({
      key: 'editNickname',
      label: '修改姓名',
      onClick: () => {
        setNicknameValue(user?.nickname || '')
        setNicknameModalVisible(true)
      }
    })
    if (hasGroup && canUploadLogs) {
      items.push({
        key: 'uploadLogs',
        label: '上报日志',
        disabled: uploadingLogs,
        onClick: handleUploadLogs
      })
    }
    items.push({
      key: 'logout',
      label: '退出登录',
      onClick: onLogout
    })
    return items
  }, [onLogout, handleUploadLogs, hasGroup, canUploadLogs, uploadingLogs])
  const onPressKeyAccountClick = () => {
    sensors.track('web_workgroup_nav_menu_cli', {
      path: '大客户定制',
      isGroupVIP: group.isVip,
      bool1: isSpaceOpen
    })

    const urlForDefault =
      'https://alidocs.dingtalk.com/notable/share/form/v01a2QnV4jrv9oKDO4X_jDtnaSu_zfBjKTB'

    const urlForTest =
      'https://alidocs.dingtalk.com/notable/share/form/v01a2QnV4jrv9oKDO4X_FKntHR9_WpJBNO9'

    const url = __RELEASE_STAGE__ === 'production' ? urlForDefault : urlForTest

    if (desktopLib.isDesktop) {
      desktopLib.openContact(url)
    } else {
      document.createElement('a')
      const a = document.createElement('a')
      a.href = url
      a.target = '_blank'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    }
  }

  const onPressEnterpriseAiUpgradeClick = () => {
    sensors.track('web_workgroup_nav_menu_cli', {
      path: '企业AI升级包',
      isGroupVIP: group.isVip,
      bool1: isSpaceOpen
    })
    setEnterpriseAiUpgradeVisible(true)
  }

  const closeEnterpriseAiUpgradeModal = useCallback(() => {
    setEnterpriseAiUpgradeVisible(false)
  }, [])
  const expireDaysMain = useMemo(() => {
    if (isVip) {
      let expireUnix = 0
      if (group.vipInfo) {
        expireUnix = group.vipInfo?.expire_time || 0
      }
      if (expireUnix) {
        return dayjs.unix(expireUnix).diff(dayjs(), 'days')
      }
    } else {
      const statistics = group.statistics
      if (statistics) {
        const stat = statistics.data ? statistics.data : statistics
        const realTime = stat.realTime
        const expireTime = stat.expireTime
        if (realTime && expireTime) {
          return dayjs.unix(expireTime).diff(dayjs.unix(realTime), 'days')
        }
      }
    }
    return 0
  }, [group, isVip])

  // 会员到期时间戳（unix 秒）· 透传给新 hover 卡展示「YYYY/MM/DD 到期」；非会员无到期
  const expireTimeMain = useMemo(() => {
    if (!isVip) return undefined
    return group.vipInfo?.expire_time
  }, [group, isVip])

  const getVipType = () => {
    // 先处理过期
    if (typeof expireDaysMain === 'number' && expireDaysMain < 0) {
      return '过期会员'
    }

    // 会员优先级：SVip > VVIP > VIP
    if (isVip) {
      // 临期优先
      if (typeof expireDaysMain === 'number' && expireDaysMain <= 30) {
        return '临期会员'
      }
      if (isSVip) return '在期SVIP'
      if (isVVip) return '在期VVIP'
      return '在期VIP'
    }

    return '非会员'
  }

  const buildPayTrackParams = () => {
    return {
      vipType: getVipType(),
      groupID: group?.groupId ?? null,
      number1:
        isVip && typeof expireDaysMain === 'number'
          ? Math.max(expireDaysMain, 0)
          : 0,
      track_sign: 'avip.b4360.action.web_click_button_menu_go_vip',
      requirement_id: 801
    }
  }

  const buildBannerTrackParams = (clickItem) => {
    return {
      clickItem,
      vipType: getVipType(),
      number1:
        isVip && typeof expireDaysMain === 'number'
          ? Math.max(expireDaysMain, 0)
          : 0,
      track_sign: 'avip.b4360.c7987.click.vip_b4360_c7987_click',
      requirement_id: 801
    }
  }

  const onPayMain = useCallback(() => {
    sensors.track('web_click_button_menu_go_vip', buildPayTrackParams())
    if (!isVip) {
      /**
       * 2月29日更新：
       * 非vip团队点击开通会员banner，分流50%自动选择超级会员商品
       */
      abtest.fastFetchABTest({
        param_name: 'web_workgroup_svip',
        default_value: false,
        value_type: 'Boolean',
        callback: (svip_ab) => {
          if (svip_ab) {
            payment.openSvip('menuResidentEntrance', false, false)
          } else {
            payment.open('menuResidentEntrance')
          }
        }
      })
    } else {
      payment.openSvip('menuResidentEntrance', false, false, isVip && !isVVip)
    }
  }, [isVip, isVVip, payment])

  const openPayment = (
    kind: number,
    isExpiration = false,
    customContent: { variant: string; data: any } | null = null
  ) => {
    sensors.track('web_click_button_menu_go_vip', buildPayTrackParams())
    // 如果没有推荐等级并且非临期会员，则根据当前等级升级到上一级
    if (kind === 0 && !isExpiration) {
      const kindMap = {
        [CONFIG.VIP_KIND.VIP]: CONFIG.VIP_KIND.VVIP,
        [CONFIG.VIP_KIND.VVIP]: CONFIG.VIP_KIND.SVIP,
        [CONFIG.VIP_KIND.SVIP]: CONFIG.VIP_KIND.EVIP,
        [CONFIG.VIP_KIND.EVIP]: CONFIG.VIP_KIND.EVIP
      }

      // 升级到当前等级上一级
      if (!isVip) {
        // 非会员兜底开 VIP tab · 用 openVipByKind 让 customContent 透传（payment.open 不接受 customContent）
        payment.openVipByKind(
          CONFIG.VIP_KIND.VIP,
          'menuResidentEntrance',
          false,
          customContent
        )
      } else {
        payment.openVipByKind(
          kindMap[currentVipKind!],
          'menuResidentEntrance',
          false,
          customContent
        )
      }
    } else {
      payment.openVipByKind(kind, 'menuResidentEntrance', false, customContent)
    }
  }

  const newOnPay = useCallback(
    (
      isBannerClick = true,
      customContent: { variant: string; data: any } | null = null
    ) => {
      sensors.track(
        'vip_b4360_c7987_click',
        buildBannerTrackParams('开通或升级')
      )
      // 原 groupWebVipAB.benifitPage 已全量：主按钮点击进权益页
      if (isBannerClick) {
        sensors.track('web_workgroup_nav_menu_cli', {
          path: '开通会员',
          groupID: group.groupId,
          isGroupVIP: group.isVip,
          bool1: isSpaceOpen
        })
        window.location.href = `/privileges?fromPlace=${encodeURIComponent(
          '首页入口'
        )}`
      } else {
        // 如果当前会员快要到期就续费当前会员
        if (expireDaysMain <= 30 && isVip) {
          openPayment(currentVipKind || 0, true, customContent)
        } else {
          // 如果没有到期就开通推荐等级
          openPayment(
            group.statistics?.topNeedVipKind || 0,
            false,
            customContent
          )
        }
      }
    },
    [isSVip, expireDaysMain, group, currentVipKind, isVip, openPayment]
  )

  const downloadDesktop = () => {
    sensors.track('web_workgroup_nav_menu_cli', {
      path: 'downloadClient',
      isGroupVIP: group.isVip,
      bool1: isSpaceOpen
    })
    window.location.href = CONFIG.DESKTOP_DOWNLOAD_URL
  }
  const handleCreateGroupe = (fromPlace = 'webTopGuide') => {
    trackCreateGuide('clickCreate')
    setCreateFromPlace(fromPlace)
    setCreateGroupModuleVisible(true)
  }
  const handleJoinGroup = (fromPlace = 'webTopGuide') => {
    trackCreateGuide('clickJoin')
    setJoinFromPlace(fromPlace)
    setJoinGroupModuleVisible(true)
  }

  return (
    <div>
      <Header className="main-header">
        {/* 登录无团队可以进入web验真页，点击其他区域创建团队 */}
        {hasGroup ? (
          <div className="header-left">
            <GroupInfo
              onClickJoin={() => handleJoinGroup('navMenu')}
              onClickCreate={() => handleCreateGroupe('navMenu')}
            />
            <div
              className="vip-container"
              onMouseEnter={handleVipMouseEnter}
              onMouseLeave={handleVipMouseLeave}
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {/* 根据实验不同展示不同的组件，使用新旧的付费代码逻辑 */}
              {showVipEntranceBanner ? (
                <VipEntranceBanner
                  onPay={(customContent) => newOnPay(false, customContent)}
                  expireDays={expireDaysMain}
                />
              ) : isGroupWebVipEntry && isVip ? (
                <>
                  <NewStatistics />
                  <BussinessArea
                    onPayMain={() => newOnPay(true)}
                    expireDays={expireDaysMain}
                    vipType={getVipType()}
                  />
                </>
              ) : group.isVip ? (
                <>
                  <Statistics />
                  <BussinessArea
                    onPayMain={onPayMain}
                    expireDays={expireDaysMain}
                    vipType={getVipType()}
                  />
                </>
              ) : (
                <>
                  <img
                    className="main-header-vip-img"
                    src={mainHeaderVipImg}
                    alt=""
                  />
                  <BussinessArea
                    onPayMain={onPayMain}
                    expireDays={expireDaysMain}
                    vipType={getVipType()}
                  />
                </>
              )}
              {!showVipEntranceHoverCard && isGroupWebVipEntry && vipHover && (
                <VipHoverPopover
                  stats={group.statistics}
                  group={group}
                  onPressKeyAccountClick={() => {
                    sensors.track(
                      'vip_b4360_c7987_click',
                      buildBannerTrackParams('大客户定制会员')
                    )
                    onPressKeyAccountClick()
                  }}
                  onPayMain={() => newOnPay(false)}
                  expireDays={expireDaysMain}
                />
              )}
              {showVipEntranceHoverCard && vipHover && (
                <VipEntranceHoverCard
                  expireDays={expireDaysMain}
                  expireTime={expireTimeMain}
                  onPay={(customContent) =>
                    // 在期（非临期）SVIP 主按钮是「会员中心」，跳 /privileges（与旧版一致）；
                    // 其余身份（含临期/过期 SVIP 的「续费会员」）走开通/续费弹窗
                    isSVip && expireDaysMain > 30
                      ? newOnPay(true)
                      : newOnPay(false, customContent)
                  }
                  onPressKeyAccount={() => {
                    sensors.track(
                      'vip_b4360_c7987_click',
                      buildBannerTrackParams('大客户定制会员')
                    )
                    onPressKeyAccountClick()
                  }}
                  onViewAllBenefits={() => newOnPay(true)}
                />
              )}
            </div>
            {isGroupWebVipEntry ? null : (
              <div className="renewal">
                <Popover
                  content={<img style={{ width: 240 }} src={hejingQrCode} />}
                >
                  <Button onClick={onPressKeyAccountClick}>大客户定制</Button>
                </Popover>
              </div>
            )}

            {/* <div className="enterprise-ai-upgrade">
              <Button
                className="enterprise-ai-upgrade-btn"
                onClick={onPressEnterpriseAiUpgradeClick}
              >
                <span
                  className="enterprise-ai-upgrade-btn__icon"
                  aria-hidden
                  style={{ transform: 'rotate(45deg)' }}
                >
                  <Icon type="rocket" />
                </span>
                <span className="enterprise-ai-upgrade-btn__text">
                  企业AI升级包
                </span>
                <span className="enterprise-ai-upgrade-btn__hot">HOT</span>
              </Button>
            </div> */}
          </div>
        ) : (
          <div className="header-left">
            <img className="create-group-img" src={createGroupeImage} alt="" />
            <Button
              type="primary"
              className="create-group-btn"
              onClick={() => handleCreateGroupe('webTopGuide')}
            >
              创建团队
            </Button>
            <Button
              className="join-group-btn"
              onClick={() => handleJoinGroup('webTopGuide')}
            >
              加入团队
            </Button>
          </div>
        )}
        {/*直播链接，图片和 链接 url 根据 key-value获取*/}
        {liveStreamingConfig.liveLink && liveStreamingConfig.imgUrl && (
          <div
            onClick={() => {
              if (desktopLib.isDesktop) {
                desktopLib.openContact(liveStreamingConfig.liveLink)
              } else {
                document.createElement('a')
                const a = document.createElement('a')
                a.href = liveStreamingConfig.liveLink
                a.target = '_blank'
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
              }
            }}
          >
            <img height={53} src={liveStreamingConfig.imgUrl} alt="" />
            {/*<a href={liveStreamingConfig.liveLink} target="_blank"></a>*/}
          </div>
        )}
        <div className="header-right">
          {!isDesktop && (
            <div className="download-desktop-btn-wrap">
              <Tooltip
                title="桌面端看图更流畅，批量下载可指定本地文件夹，管理照片更省心"
                placement="bottom"
                mouseEnterDelay={0.3}
              >
                <Button
                  className="download-desktop-btn"
                  onClick={downloadDesktop}
                  aria-label="下载今日水印相机电脑客户端"
                >
                  <VerticalAlignBottomOutlined />
                  下载电脑端
                </Button>
              </Tooltip>
              <span className="download-desktop-btn__badge">推荐</span>
            </div>
          )}

          {/* <UploadButton /> */}

          {hasGroup && <UploadPhotosButton />}

          {hasGroup && <NotifyButton />}

          <ContactUS
            fromPage="main_header"
            style={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <Tooltip title="联系客服" placement="bottom">
              <span
                className="custom-service-btn"
                onClick={() => {
                  sensors.track('web_workgroup_nav_menu_cli', {
                    path: 'onlineSupport',
                    isGroupVIP: group.isVip,
                    bool1: isSpaceOpen
                  })
                }}
              >
                <img
                  src={global_border_help}
                  style={{ width: 24, height: 24, display: 'block' }}
                  alt="联系客服"
                />
              </span>
            </Tooltip>
          </ContactUS>
          {/* 用户头像菜单 · 加 mouseEnter/Leave 延迟防误触 (master ce58cbda) */}
          <Dropdown
            menu={{ items: menuItems }}
            mouseEnterDelay={0.35}
            mouseLeaveDelay={0.25}
          >
            <a>
              <Avatar
                shape="square"
                size={30}
                style={{ marginLeft: 18, borderRadius: 2 }}
                src={user?.headimgurl}
              />
              <DownOutlined
                style={{
                  marginLeft: 6,
                  marginRight: 20,
                  color: '#fff',
                  opacity: 0.6
                }}
              />
            </a>
          </Dropdown>
        </div>
      </Header>
      <EnterpriseAiUpgradeModal
        visible={enterpriseAiUpgradeVisible}
        onCancel={closeEnterpriseAiUpgradeModal}
      />
      <Modal
        open={createGroupModuleVisible}
        footer={null}
        closable={true}
        mask={{ closable: true }}
        width={394}
        style={{ top: '20%' }}
        onCancel={() => setCreateGroupModuleVisible(false)}
      >
        <CreateGroup
          fromPlace={createFromPlace}
          onGotoJoin={() => {
            setCreateGroupModuleVisible(false)
            handleJoinGroup('createGroup')
          }}
        />
      </Modal>
      <Modal
        open={joinGroupModuleVisible}
        footer={null}
        closable={true}
        mask={{ closable: true }}
        width={394}
        className="join-group-modal"
        style={{ top: '20%' }}
        styles={{ body: { padding: 0, height: 516 } }}
        onCancel={() => setJoinGroupModuleVisible(false)}
        destroyOnHidden
      >
        <JoinGroup
          fromPlace={joinFromPlace}
          onClose={() => setJoinGroupModuleVisible(false)}
          onGotoCreate={() => {
            setJoinGroupModuleVisible(false)
            handleCreateGroupe('joinGroup')
          }}
        />
      </Modal>
      <Modal
        open={nicknameModalVisible}
        footer={null}
        closable
        centered
        width={340}
        onCancel={() => {
          if (!user?.nickname) {
            const randomName =
              '用户' + (Math.floor(Math.random() * 9000) + 1000)
            dispatch(finishNickname({ userID: userId, nickname: randomName }))
          }
          setNicknameModalVisible(false)
          setNicknameValue('')
        }}
        destroyOnHidden
      >
        <div style={{ textAlign: 'center', padding: '32px 8px 16px' }}>
          <img
            src={todaycam_logo}
            alt="今日水印相机"
            style={{ width: 72, height: 72, marginBottom: 20 }}
          />
          <h3
            style={{
              fontSize: 18,
              fontWeight: 600,
              marginBottom: 40,
              color: '#1d2129'
            }}
          >
            完善个人信息
          </h3>
          <Input
            placeholder="请输入姓名"
            value={nicknameValue}
            onChange={handleNicknameInputChange}
            style={{ marginBottom: 12 }}
          />
          <p
            style={{
              color: '#86909c',
              fontSize: 13,
              textAlign: 'left',
              marginBottom: 56
            }}
          >
            填写姓名，同事一眼认出你
          </p>
          <Button
            type="primary"
            block
            disabled={!nicknameValue}
            onClick={handleNicknameSubmit}
          >
            完成
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default MainHeader
